import { differenceInCalendarDays, format, isSameDay } from 'date-fns';
import type { Notification } from '../../../contexts/NotificationContext';

export type UpdateKind = 'checkin' | 'nps' | 'leave' | 'nomination' | 'other';
export type LeaveOutcome = 'approved' | 'denied' | 'cancelled' | 'updated';

/** One line in the Updates list. */
export interface UpdateView {
  id: string;
  kind: UpdateKind;
  title: string;
  subtitle?: string;
  /** Who the update is about; folded groups list these. */
  subject: string;
  date: Date;
  read: boolean;
  /** Where clicking the update goes. */
  href: string;
  npsRating?: number;
  leaveOutcome?: LeaveOutcome;
}

export type UpdateEntry =
  | { type: 'item'; item: UpdateView }
  | { type: 'fold'; key: string; kind: UpdateKind; items: UpdateView[] };

export interface UpdateGroup {
  label: string;
  entries: UpdateEntry[];
}

/** Same-kind updates in one day fold into a single expandable line from this many. */
export const FOLD_THRESHOLD = 3;

/**
 * Whether a notification belongs under Updates. Leave requests are shown from
 * the requests themselves in "Waiting on you", and so are success nominations
 * until they have been opened.
 */
export const isUpdateNotification = (notification: Notification): boolean =>
  notification.type !== 'leave_request_submitted' &&
  !(notification.type === 'success_nomination' && !notification.read);

const subjectOf = (notification: Notification): string =>
  notification.client?.name || notification.clientName || 'Unknown client';

const toDate = (notification: Notification): Date => notification.timestamp?.toDate?.() ?? new Date(0);

/** "2 Week" (as written by the check-in trigger) becomes "2-week check-in". */
export const checkInLabel = (raw?: string): string => {
  const weeks = raw?.match(/(\d+)\s*week/i);
  if (weeks) return `${weeks[1]}-week check-in`;
  return raw ? `${raw} check-in` : 'Check-in';
};

export const leaveOutcomeOf = (raw?: string): LeaveOutcome => {
  const value = (raw || '').toLowerCase();
  if (value.includes('approv')) return 'approved';
  if (value.includes('denied') || value.includes('reject')) return 'denied';
  if (value.includes('cancel')) return 'cancelled';
  return 'updated';
};

const quote = (text?: string) => (text ? `“${text.trim()}”` : undefined);

export const describeUpdate = (notification: Notification, viewerUid?: string | null): UpdateView => {
  const subject = subjectOf(notification);
  const clientId = notification.client?.id || notification.clientId;
  const meta = notification.metadata || {};
  const base = { id: notification.id, subject, date: toDate(notification), read: notification.read };

  switch (notification.type) {
    case 'manager_checkin':
      return {
        ...base,
        kind: 'checkin',
        title: `${subject} · ${checkInLabel(meta.checkinType)}`,
        subtitle: meta.submittedByName ? `By ${meta.submittedByName}` : undefined,
        href: `/clients/${clientId}?tab=check-ins`,
      };
    case 'nps_submission':
      return {
        ...base,
        kind: 'nps',
        title: meta.npsRating !== undefined ? `${subject} rated ${meta.npsRating}/5` : `${subject} answered the NPS survey`,
        subtitle: quote(meta.excerpt),
        href: `/clients/${clientId}`,
        npsRating: meta.npsRating,
      };
    case 'leave_status_update': {
      const outcome = leaveOutcomeOf(meta.checkinType);
      const staff = meta.staffName || subject;
      const actor = meta.submittedByName || 'A manager';
      const isMine = Boolean(viewerUid) && meta.staffUid === viewerUid;
      let title: string;
      if (isMine) title = `Your leave was ${outcome}`;
      else if (outcome === 'cancelled') title = `${staff} cancelled leave`;
      else title = `${actor} ${outcome} ${staff}’s leave`;
      return {
        ...base,
        subject: staff,
        kind: 'leave',
        title,
        subtitle: isMine ? `By ${actor}` : undefined,
        href: '/leave-manager',
        leaveOutcome: outcome,
      };
    }
    case 'success_nomination':
      return {
        ...base,
        kind: 'nomination',
        title: `${subject} nominated by ${meta.submittedByName || 'a team member'}`,
        subtitle: quote(meta.excerpt),
        href: meta.nominationId ? `/success-nominations?highlight=${meta.nominationId}` : '/success-nominations',
      };
    default:
      return { ...base, kind: 'other', title: notification.message || subject, href: clientId ? `/clients/${clientId}` : '/dashboard' };
  }
};

export const dayLabel = (date: Date, now: Date): string => {
  const days = differenceInCalendarDays(now, date);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return format(date, 'EEEE');
  return 'Earlier';
};

/** "Thu 5 – Fri 6 Nov", "Fri 30 Oct – Mon 2 Nov", or one day: "Thu 5 Nov". */
export const formatLeaveRange = (start: Date, end: Date): string => {
  if (isSameDay(start, end)) return format(start, 'EEE d MMM');
  if (start.getFullYear() !== end.getFullYear()) {
    return `${format(start, 'EEE d MMM yyyy')} – ${format(end, 'EEE d MMM yyyy')}`;
  }
  if (start.getMonth() === end.getMonth()) return `${format(start, 'EEE d')} – ${format(end, 'EEE d MMM')}`;
  return `${format(start, 'EEE d MMM')} – ${format(end, 'EEE d MMM')}`;
};

export const formatDayCount = (days: number): string => `${days} ${days === 1 ? 'day' : 'days'}`;

/** When something was asked for: a time today, a weekday this week, otherwise a date. */
export const sinceLabel = (date: Date, now: Date): string => {
  const days = differenceInCalendarDays(now, date);
  if (days <= 0) return format(date, 'h:mm a').toLowerCase();
  if (days < 7) return format(date, 'EEE');
  return format(date, 'd MMM');
};

/** Time of day within a day group; a date once updates are older than a week. */
export const timeLabel = (date: Date, groupLabel: string): string =>
  groupLabel === 'Earlier' ? format(date, 'd MMM') : format(date, 'h:mm a').toLowerCase();

// A low NPS score is an exception worth its own line, so it never folds.
const foldKey = (update: UpdateView): string | null => {
  if (update.kind === 'other') return null;
  if (update.kind === 'nps' && (update.npsRating ?? 5) <= 2) return null;
  return update.kind;
};

const FOLD_NOUNS: Record<UpdateKind, [string, string]> = {
  checkin: ['check-in', 'check-ins'],
  nps: ['NPS rating', 'NPS ratings'],
  leave: ['leave update', 'leave updates'],
  nomination: ['nomination', 'nominations'],
  other: ['update', 'updates'],
};

export const foldTitle = (kind: UpdateKind, count: number): string =>
  `${count} ${FOLD_NOUNS[kind][count === 1 ? 0 : 1]}`;

/** "Marcus Chua, Hafiz Ismail, Joanne Ng +2", naming each subject once. */
export const foldSubtitle = (items: UpdateView[], shown = 3): string => {
  const names = Array.from(new Set(items.map((item) => item.subject)));
  const head = names.slice(0, shown).join(', ');
  return names.length > shown ? `${head} +${names.length - shown}` : head;
};

/**
 * Groups updates (newest first) by day, folding FOLD_THRESHOLD or more
 * same-kind updates in a day into one entry at the position of the newest.
 */
export const groupUpdates = (updates: UpdateView[], now: Date, threshold = FOLD_THRESHOLD): UpdateGroup[] => {
  const byDay: UpdateGroup[] = [];
  const dayItems = new Map<string, UpdateView[]>();
  updates.forEach((update) => {
    const label = dayLabel(update.date, now);
    if (!dayItems.has(label)) {
      dayItems.set(label, []);
      byDay.push({ label, entries: [] });
    }
    dayItems.get(label)!.push(update);
  });

  return byDay.map(({ label }) => {
    const items = dayItems.get(label)!;
    const counts = new Map<string, number>();
    items.forEach((item) => {
      const key = foldKey(item);
      if (key) counts.set(key, (counts.get(key) || 0) + 1);
    });

    const entries: UpdateEntry[] = [];
    const placed = new Set<string>();
    items.forEach((item) => {
      const key = foldKey(item);
      if (key && (counts.get(key) || 0) >= threshold) {
        if (placed.has(key)) return;
        placed.add(key);
        entries.push({
          type: 'fold',
          key: `${label}:${key}`,
          kind: item.kind,
          items: items.filter((other) => foldKey(other) === key),
        });
        return;
      }
      entries.push({ type: 'item', item });
    });
    return { label, entries };
  });
};
